import React, { useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { aiJobStatusVariant } from '@/lib/status-variants'
import { LoaderCircle, XCircle, CheckCircle, AlertCircle } from 'lucide-react'
import { Query, Mutation } from '../../../../api'
import { useModuleActions } from '../../../../hooks/useMyPermissions'

interface Props {
  jobId: string
  onComplete: (jobType?: string, jobStatus?: string) => void
  projectId?: string
}

const AiProcessingProgress: React.FC<Props> = ({ jobId, onComplete, projectId }) => {
  const { t } = useTranslation()
  const { canEdit } = useModuleActions('AI_EVALUATION', projectId)
  const completedRef = useRef(false)

  const isTerminal = (status: string) =>
    status === 'COMPLETED' || status === 'FAILED' || status === 'CANCELLED'

  const { data, loading, stopPolling } = useQuery(Query.AI_PROCESSING_JOB, {
    variables: { id: jobId },
    pollInterval: 2000,
  })

  const job = data?.aiProcessingJob

  // Stop polling and notify parent when job reaches a terminal state
  useEffect(() => {
    if (job && !completedRef.current && isTerminal(job.status)) {
      stopPolling()
      completedRef.current = true
      onComplete(job.type, job.status)
    }
  }, [job?.status])

  const [cancelJob, { loading: cancelling }] = useMutation(Mutation.CANCEL_AI_JOB)

  const handleCancel = async () => {
    try {
      await cancelJob({ variables: { jobId } })
    } catch (err) {
      console.error('Failed to cancel job:', err)
    }
  }

  if (loading && !job) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8 text-muted-foreground">
          <LoaderCircle className="h-5 w-5 animate-spin mr-2" />
          {t('loading')}
        </CardContent>
      </Card>
    )
  }

  if (!job) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8 text-muted-foreground">
          {t('ai_job_not_found')}
        </CardContent>
      </Card>
    )
  }

  const progress = Math.round(job.progress || 0)
  const isActive = job.status === 'PROCESSING' || job.status === 'PENDING'

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 p-4">
        <CardTitle className="text-base">{t('ai_processing_progress')}</CardTitle>
        <Badge variant={aiJobStatusVariant(job.status)} className="font-medium whitespace-nowrap">
          {job.status}
        </Badge>
      </CardHeader>
      <CardContent className="p-4 pt-0 space-y-4">
        {/* Progress bar */}
        <div>
          <div className="flex items-center justify-between mb-1.5 text-sm">
            <span>{job.currentStep || t('ai_processing')}</span>
            <span className="text-muted-foreground">{progress}%</span>
          </div>
          <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full transition-all',
                job.status === 'FAILED' ? 'bg-destructive' :
                job.status === 'COMPLETED' ? 'bg-green-500' :
                'bg-primary',
              )}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Error message */}
        {job.error && (
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{job.error}</span>
          </div>
        )}

        {/* Cancel button */}
        {job.status === 'PROCESSING' && canEdit && (
          <Button
            variant="destructive"
            size="sm"
            onClick={handleCancel}
            disabled={cancelling}
          >
            {cancelling ? (
              <LoaderCircle className="h-4 w-4 animate-spin" />
            ) : (
              <XCircle className="h-4 w-4" />
            )}
            {t('ai_cancel_job')}
          </Button>
        )}

        {/* Processing logs */}
        {job.logs && job.logs.length > 0 && (
          <div>
            <h6 className="text-sm font-semibold mb-2">{t('ai_processing_logs')}</h6>
            <div className="max-h-[300px] overflow-y-auto rounded-md border divide-y">
              {job.logs.map((log: any) => (
                <div
                  key={log.id}
                  className="flex items-start gap-2 px-3 py-1.5 text-[0.85rem]"
                >
                  <span className="text-muted-foreground shrink-0">
                    {new Date(log.timestamp).toLocaleTimeString()}
                  </span>
                  {log.level === 'ERROR' && (
                    <Badge variant="danger" className="font-medium whitespace-nowrap text-[0.7rem] py-0">
                      ERR
                    </Badge>
                  )}
                  <span>{log.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default AiProcessingProgress
